import { inject, Injectable, InjectionToken } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { ROOT_CONTEXT, Span, SpanKind, SpanStatusCode, trace, Tracer, type Context } from '@opentelemetry/api';
import { scrubSensitiveParams } from '@nestech/utils';
import { isTelemetryInitialized, setNavigationContext, setNavigationSpanProcessorCallbacks } from './instrumentation';
import type { NavigationId, NavigationSpanProcessorCallbacks } from './navigation-span-processor';

const TRACER_NAME = 'page-navigation';
const TRACER_VERSION = '1.0.0';
const NETWORK_IDLE_DELAY_IN_MS = 500;

/**
 * The OpenTelemetry SDK lives in module-level singletons set up before Angular bootstraps, which a
 * test cannot substitute; this token is the seam that lets it.
 */
export interface TelemetrySdk {
  isTelemetryInitialized: () => boolean;
  setNavigationContext: (context: Context | null) => void;
  setNavigationSpanProcessorCallbacks: (callbacks: NavigationSpanProcessorCallbacks) => void;
  getTracer: () => Tracer;
}

export const TELEMETRY_SDK = new InjectionToken<TelemetrySdk>('TELEMETRY_SDK', {
  factory: () => ({
    isTelemetryInitialized,
    setNavigationContext,
    setNavigationSpanProcessorCallbacks,
    getTracer: () => trace.getTracer(TRACER_NAME, TRACER_VERSION),
  }),
});

interface NavigationContext {
  id: NavigationId;
  span: Span;
  context: Context;
  startTimeInMs: number;
  pendingRequestCount: number;
  settleTimeoutId: ReturnType<typeof setTimeout> | null;
  isNetworkIdleReported: boolean;
}

@Injectable({ providedIn: 'root' })
export class TelemetryService {
  readonly #router = inject(Router);
  readonly #sdk = inject(TELEMETRY_SDK);
  readonly #tracer: Tracer = this.#sdk.getTracer();

  #navigation: NavigationContext | null = null;
  #nextNavigationId: NavigationId = 1;

  constructor() {
    if (!this.#sdk.isTelemetryInitialized()) return;

    this.#instrumentRouter();
    this.#sdk.setNavigationSpanProcessorCallbacks({
      onRequestStart: () => this.#onRequestStart(),
      onRequestEnd: owningNavigationId => this.#onRequestEnd(owningNavigationId),
    });
  }

  // ─── Network Activity Tracking ───────────────────────────────────────────────

  #onRequestStart(): NavigationId | null {
    if (!this.#navigation || this.#navigation.isNetworkIdleReported) return null;

    this.#navigation.pendingRequestCount++;
    this.#clearSettleTimeout(this.#navigation);

    return this.#navigation.id;
  }

  /**
   * A request outlives the navigation that issued it, so requests are accounted to their owner
   * rather than to whichever navigation happens to be current. Without this, the late responses of
   * a superseded navigation drive its successor's counter to zero and settle it early, stamping a
   * duration that excludes the successor's own load.
   */
  #onRequestEnd(owningNavigationId: NavigationId) {
    const navigation = this.#navigation;
    if (!navigation || navigation.id !== owningNavigationId) return;

    navigation.pendingRequestCount = Math.max(0, navigation.pendingRequestCount - 1);
    this.#scheduleNetworkIdleCheck();
  }

  // ─── Router Instrumentation ────────────────────────────────────────────────

  #instrumentRouter() {
    this.#router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.#onNavigationStart(event.url);
      } else if (event instanceof NavigationEnd) {
        this.#onNavigationEnd();
      } else if (event instanceof NavigationCancel) {
        this.#endNavigation('cancelled');
      } else if (event instanceof NavigationError) {
        this.#endNavigation('error');
      }
    });
  }

  #onNavigationStart(url: string) {
    this.#endNavigation('superseded');

    // ROOT_CONTEXT keeps the navigation span a root span rather than nesting it under whatever span
    // was active; publishing it as the navigation context is what parents HTTP spans under it.
    const span = this.#tracer.startSpan(
      `route-${this.#routeNameFromUrl(url)}`,
      {
        kind: SpanKind.CLIENT,
        // The OIDC callback carries a live bearer JWT and refresh token in its query string.
        attributes: { 'page.url': scrubSensitiveParams(url) ?? url },
      },
      ROOT_CONTEXT,
    );

    const navContext = trace.setSpan(ROOT_CONTEXT, span);
    this.#sdk.setNavigationContext(navContext);

    this.#navigation = {
      id: this.#nextNavigationId++,
      span,
      context: navContext,
      startTimeInMs: performance.now(),
      pendingRequestCount: 0,
      settleTimeoutId: null,
      isNetworkIdleReported: false,
    };

    span.addEvent('navigation-start');
  }

  #onNavigationEnd() {
    if (!this.#navigation) return;

    const routeName = this.#extractRouteName();

    this.#navigation.span.updateName(`route-${routeName}`);
    this.#navigation.span.setAttribute('route', routeName);

    this.#navigation.span.addEvent('navigation-end', {
      'navigation.duration_ms': performance.now() - this.#navigation.startTimeInMs,
    });

    this.#scheduleNetworkIdleCheck();
  }

  #extractRouteName(): string {
    const segments: string[] = [];

    let route: ActivatedRouteSnapshot | null = this.#router.routerState.snapshot.root;
    while (route) {
      const path = route.routeConfig?.path;
      if (path && !path.startsWith(':')) {
        segments.push(path);
      }
      route = route.firstChild;
    }

    return segments.filter(Boolean).join('-') || 'root';
  }

  #endNavigation(reason: 'superseded' | 'cancelled' | 'error') {
    if (!this.#navigation) return;

    this.#clearSettleTimeout(this.#navigation);

    if (!this.#navigation.isNetworkIdleReported) {
      this.#navigation.span.addEvent('navigation-ended', {
        reason,
        pending_requests: this.#navigation.pendingRequestCount,
      });
      this.#navigation.span.setStatus({
        code: reason === 'error' ? SpanStatusCode.ERROR : SpanStatusCode.OK,
        message: reason === 'error' ? 'Navigation error' : undefined,
      });
      this.#navigation.span.end();
      this.#sdk.setNavigationContext(null);
    }

    this.#navigation = null;
  }

  // ─── Network Idle Detection ────────────────────────────────────────────────

  #scheduleNetworkIdleCheck() {
    if (!this.#navigation || this.#navigation.isNetworkIdleReported) return;

    this.#clearSettleTimeout(this.#navigation);

    if (this.#navigation.pendingRequestCount === 0) {
      this.#navigation.settleTimeoutId = setTimeout(() => this.#reportNetworkIdle(), NETWORK_IDLE_DELAY_IN_MS);
    }
  }

  #clearSettleTimeout(navigation: NavigationContext) {
    if (navigation.settleTimeoutId) {
      clearTimeout(navigation.settleTimeoutId);
      navigation.settleTimeoutId = null;
    }
  }

  #reportNetworkIdle() {
    if (!this.#navigation || this.#navigation.isNetworkIdleReported) return;

    this.#navigation.isNetworkIdleReported = true;
    const durationInMs = performance.now() - this.#navigation.startTimeInMs;

    this.#navigation.span.addEvent('network-idle', { total_duration_ms: durationInMs });
    this.#navigation.span.setAttribute('navigation.duration_ms', durationInMs);

    // A navigation span is a discrete unit of work (route change -> data loaded); holding it open
    // until the next navigation would render misleadingly long spans in the waterfall.
    this.#navigation.span.setStatus({ code: SpanStatusCode.OK });
    this.#navigation.span.end();
    this.#sdk.setNavigationContext(null);
  }

  // ─── Utilities ─────────────────────────────────────────────────────────────

  /**
   * Names a span before the route is recognised, so it is never left as the raw URL. Drops
   * query/hash and path params: '/person/123/2026-02-01/engagement' → 'person-engagement'.
   */
  #routeNameFromUrl(url: string): string {
    const path = url.split(/[?#]/)[0];
    return (
      path
        .split('/')
        .filter(
          segment =>
            segment &&
            !/^\d+$/.test(segment) &&
            !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment) &&
            !/^\d{4}-\d{2}-\d{2}$/.test(segment),
        )
        .join('-') || 'root'
    );
  }
}
